import {
  type ColumnDef,
  type ColumnResizeMode,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { DataTablePagination } from './data-table-pagination';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  initialSorting?: SortingState;
  onRowClick?: (row: TData) => void;
  /** Enable server-side pagination. When true, `data` is the current page only. */
  manualPagination?: boolean;
  /** Total row count across all pages (required when manualPagination is true). */
  rowCount?: number;
  /** Controlled pagination state (required when manualPagination is true). */
  pagination?: PaginationState;
  /** Callback when the user changes page or page size. */
  onPaginationChange?: (pagination: PaginationState) => void;
  /** Enable server-side sorting. When true, sorting is delegated to the caller. */
  manualSorting?: boolean;
  /** Controlled sorting state (used when manualSorting is true). */
  sorting?: SortingState;
  /** Callback when the user changes sort column or direction. */
  onSortingChange?: (sorting: SortingState) => void;
  /**
   * Body text when there are no rows to render. Lets a caller distinguish
   * "nothing exists yet" from "the filters excluded everything" instead of
   * short-circuiting the whole table and losing the headers.
   */
  emptyMessage?: React.ReactNode;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  initialSorting = [],
  onRowClick,
  manualPagination,
  rowCount,
  pagination: controlledPagination,
  onPaginationChange,
  manualSorting,
  sorting: controlledSorting,
  onSortingChange,
  emptyMessage = 'No results.',
}: DataTableProps<TData, TValue>) {
  const isSortingControlled = controlledSorting !== undefined;
  const isPaginationControlled = controlledPagination !== undefined;
  const [internalSorting, setInternalSorting] = useState<SortingState>(initialSorting);
  const sorting = isSortingControlled ? controlledSorting : internalSorting;
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const pagination = isPaginationControlled ? controlledPagination : internalPagination;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(!manualPagination && { getPaginationRowModel: getPaginationRowModel() }),
    autoResetPageIndex: false,
    enableMultiSort: false,
    ...(manualSorting && { manualSorting: true }),
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (!isSortingControlled) {
        setInternalSorting(next);
      }
      onSortingChange?.(next);
    },
    ...(!manualSorting && { getSortedRowModel: getSortedRowModel() }),
    columnResizeMode,
    enableColumnResizing: true,
    ...(manualPagination && {
      manualPagination: true,
      rowCount,
    }),
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      if (!isPaginationControlled) {
        setInternalPagination(next);
      }
      onPaginationChange?.(next);
    },
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className='space-y-4'>
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className='relative'
                    >
                      {header.isPlaceholder ? null : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none ${
                            header.column.getIsResizing()
                              ? 'bg-primary opacity-100'
                              : 'bg-border opacity-0 hover:opacity-100'
                          }`}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length
              ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )
              : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className='h-24 text-center'
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
